import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AuthService } from '@modules/auth/auth-service';
import { SwalService } from '@shared/services/swal.service';
import { passwordStrengthValidator } from '@modules/auth/validators/password-strength.validator';
import { ModalHeaderComponent } from '../modal-header/modal-header.component';
import { TranslatePipe } from '@shared/i18n/translate.pipe';
import { LocalStorageService } from 'app/storage/local-storage-service';

@Component({
  selector: 'app-change-password-dialog',
  templateUrl: './change-password-dialog.component.html',
  styleUrls: ['./change-password-dialog.component.scss'],
  imports: [
    CommonModule,
    MatDialogModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    ModalHeaderComponent,
    TranslatePipe,
  ],
})
export class ChangePasswordDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<ChangePasswordDialogComponent>);
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly swalService = inject(SwalService);
  protected readonly data = inject(MAT_DIALOG_DATA, { optional: true });
  private readonly localStorage = inject(LocalStorageService);

  submitting = false;
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  form = this.fb.group({
    currentPassword: this.fb.nonNullable.control('', { validators: [Validators.required] }),
    newPassword: this.fb.nonNullable.control('', {
      validators: [Validators.required, passwordStrengthValidator()],
    }),
    confirmPassword: this.fb.nonNullable.control('', { validators: [Validators.required] }),
  });

  toggleShowCurrentPassword(): void {
    this.showCurrentPassword = !this.showCurrentPassword;
  }

  toggleShowNewPassword(): void {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleShowConfirmPassword(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  close(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const currentPassword = this.form.controls.currentPassword.value;
    const newPassword = this.form.controls.newPassword.value;
    const confirmPassword = this.form.controls.confirmPassword.value;

    if (newPassword !== confirmPassword) {
      this.form.controls.confirmPassword.setErrors({ mismatch: true });
      this.form.controls.confirmPassword.markAsTouched();
      return;
    }

    this.submitting = true;
    this.authService
      .changePassword({
        currentPassword,
        newPassword,
        confirmPassword,
      })
      .subscribe({
        next: (response) => {
          this.submitting = false;
          const res = response.data?.changePassword;
          if (res?.status) {
            this.swalService.successToast(res.message ?? 'Password changed successfully');
            const profile = this.localStorage.getUserProfile();
            if (profile) {
              profile.requireNewPassword = false;
              this.localStorage.saveUserProfile(profile);
            }
            this.dialogRef.close({ success: true });
            return;
          }
          this.swalService.error(res?.message ?? 'Failed to change password');
        },
        error: (error) => {
          this.submitting = false;
          this.swalService.error(error?.message ?? 'Failed to change password');
        },
      });
  }
}
